import { B2BOutboundForm } from '@/pages/outbound/outboundWaitingStatus/components/B2BContentWrapper'
import { AgGridRowId } from '@/services/common/types'
import { ExcelDownloadType } from '@/services/excel/excel.types'
import { B2B_ORDER_TYPES, ORDER_TYPES } from './consts'

export type OrderStatusType = 'ORDER_COLLECTED' | 'ORDER_ACCEPTED' | 'INV_UNALLOCATED' | 'HOLD'
export type OrderType = (typeof ORDER_TYPES)[keyof typeof ORDER_TYPES]
export type TabType = 'B2C' | 'B2B'

export interface CenterData {
  warehouseName: string
  warehouseCode: string
  orderCount: number
  orderPcs: number
}

export interface OrderItem extends AgGridRowId {
  orderStatus?: string // 주문상태
  shipperCode: string // 화주코드 — standard_order UK 선두축. 화주명과 달리 유니크하다
  shipperName: string
  logisticsServiceType: string
  orderNumber: string
  orderOptionNumber: string
  refNo1?: string
  refNo2?: string
  etc1?: string
  etc2?: string
  orderType: OrderType
  transportType: string
  collectedDate: string
  orderDateTime: string
  barcode: string
  skuId: string | null
  uid: string
  brandName: string
  goodsCode: string
  goodsOption: string
  goodsName: string
  orderQuantity: number
  unallocatedQuantity: number
  styleNo: string
  optionCode: string
  gift: string
  deliveryCompletePlanDate: string
  orderUserName: string
  receiverName: string
  receiverAddress1: string
  receiverAddress2: string
  receiverZipcode: string
  receiverPhone: string
  receiverMobile: string
  deliveryPass: string
  deliveryMessage: string
  isHolidayDeliveryAvailable: boolean
  /** 미할당 운영자 재주입 가능 여부. 서버가 조회 시점의 옵티마이저 정책으로 판정한다. */
  optimizerTarget: boolean
}

export interface B2BOrderItem extends AgGridRowId {
  outboundDocumentNumber: string
  orderStatus: Exclude<OrderStatusType, 'INV_UNALLOCATED'>
  shipperCode: string
  shipperName: string
  plannedCutoffDate: string
  orderType: OrderType
  transportType: string
  orderQuantity: number
  createdAt: string
}

export type GetOrdersReq = {
  shipperNames: string[] | null
  logisticsServiceTypes: string[] | null
  orderStatus?: OrderStatusType[]
  collectedDateFrom?: string
  collectedDateTo?: string
  orderNumbers?: string[]
  orderOptionNumbers?: string[]
  barcodes?: string[]
  cutType: TabType
}

export type B2BOrderType = keyof typeof B2B_ORDER_TYPES // MEMO: B2B 탭에서는 사내, 사내(해외) 주문유형만 사용

export type B2BGetOrdersReq = {
  logisticsServiceType: string
  shipperNames?: string[]
  outboundDocumentNumbers?: string[]
  plannedCutoffDateFrom?: string
  plannedCutoffDateTo?: string
  orderType?: B2BOrderType
}

export type GetB2BOutboundDocumentDetailReq = {
  outboundDocumentNumber: string
}

export type GetB2BOutboundDocumentDetailRes = {
  brandName?: string
  styleNo?: string
  goodsName?: string
  uid?: string
  optionCode?: string
  barcode?: string
  skuId: string | null
  goodsOption?: string
  orderQuantity: number
  receiverName: string
  receiverAddress: string
  receiverZipcode: string
  receiverMobile: string
  receiverPhone?: string
  deliveryMessage?: string
}

// URL 파라미터용 타입들 (배열 타입을 문자열로 변환)
type GetOrdersUrlParams = StringifyArrays<Omit<GetOrdersReq, 'cutType'>>
type B2BOutboundUrlParams = StringifyArrays<B2BOutboundForm>

// URL 파라미터용 통합 타입
export type OutboundWaitingUrlParams = ({ tabType: 'B2C' } & GetOrdersUrlParams) | ({ tabType: 'B2B' } & B2BOutboundUrlParams)

// 타입 가드 함수들
export type B2CUrlParams = { tabType: 'B2C' } & Partial<GetOrdersUrlParams>
export type B2BUrlParams = { tabType: 'B2B' } & Partial<B2BOutboundUrlParams> // MEMO: 화면에서 조작되는 logisticsServiceTypes 타입 추가

export function isB2CParams(params: Partial<OutboundWaitingUrlParams>): params is B2CUrlParams {
  return params.tabType === 'B2C' || !params.tabType
}

export function isB2BParams(params: Partial<OutboundWaitingUrlParams>): params is B2BUrlParams {
  return params.tabType === 'B2B'
}

export type GetExpectedAllocationReq = {
  cutType: TabType
  logisticsServiceType: string | null
  shipperNames: string[] | null
}

export type GetExpectedAllocationRes = {
  items: ExpectedAllocationItems[]
}

export type ExpectedAllocationItems = {
  totalOrderCount: number
  totalOrderPcs: number
  totalNotAllocatedOrderCount: number
  totalNotAllocatedOrderPcs: number
  centerData: CenterData[]
}

export type GetOrdersDownload = ApiPaginationReq<GetOrdersReq> & Partial<ExcelDownloadType>

export type RunStandardOrderCutoffReq = {
  orderNumbers: string[]
}

// 시간대별 출고대기 수량 (B2C, 1시간 단위 9슬롯)
export type OutboundWaitingTimeSlotItem = {
  slotStartAt: string // 'HH:mm'
  slotEndAt: string // 'HH:mm'
  orderPcs: number
  orderCount: number
}

export type GetOutboundWaitingByTimeSlotReq = {
  logisticsServiceType: string | null
  shipperNames: string[] | null
}

export type GetOutboundWaitingByTimeSlotRes = {
  totalOrderPcs: number
  totalOrderCount: number
  slots: OutboundWaitingTimeSlotItem[]
}

/** get-orders API 를 부르는 화면 — 감사 로그 화면코드(X-Menu-Code) */
export type OrdersMenuCode = 'GI23' | 'GI26'
